import { useSocket } from '../utils/SocketContext';
import { fetchDeviceAlerts } from '../features/dashboardSlice';
import { getTimeAgo } from '../utils/dateUtils';

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDevices, fetchSummaryCounts, setDashboardSearch, setDashboardPage, setDashboardSort, setDeviceName, setDeviceId } from '../features/authSlice';
import { Outlet } from 'react-router-dom';
import '../styles/dashboard.css';
import Pagination from '../components/Pagination';
import searchIcon from '../assets/images/search.svg';
import locationIcon from '../assets/images/location_icon.svg';

import totalUserIcon from '../assets/images/total_user.svg';
import userCheckIcon from '../assets/images/user_check_icon.svg';
import deviceOnIcon from '../assets/images/wifi_on_icon.svg';
import deviceOffIcon from '../assets/images/wifi_off_icon.svg';
import alertTriangleIcon from '../assets/images/alert_triangle_icon.svg';
import CommanTable from '../components/CommanTable';
import { hexToCssFilter } from '../utils/hexToCssFilter';
import CustomDropdown from '../utils/customDropdown';
import Loader from '../utils/globalApiLoader';


const ALERT_FILTER_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'TANKCLEAN', label: 'Tank Cleaning' },
  { value: 'WATERLEVEL ', label: 'Water Level' },
  { value: 'TDS', label: 'TDS' },
  { value: 'SOLARBATTERY', label: 'Solar Battery' },
];

const Dashboard = () => {
  const [role, setRole] = useState('ADMIN');
  // Recent alert search state
  const [alertSearch, setAlertSearch] = useState('');
  const [debouncedAlertSearch, setDebouncedAlertSearch] = useState('');
  const alertSearchTimeout = useRef();
  // Debounce for recent alert search
  useEffect(() => {
    if (alertSearchTimeout.current) clearTimeout(alertSearchTimeout.current);
    alertSearchTimeout.current = setTimeout(() => {
      setDebouncedAlertSearch(alertSearch);
    }, 500);
    return () => clearTimeout(alertSearchTimeout.current);
  }, [alertSearch]);
  // Alert filter state
  const [alertType, setAlertType] = useState('');
  const navigate = useNavigate();

  const dispatch = useDispatch();
  // Expecting state.auth.devices = { devices: [...], total: 212 }
  const reduxDevices = useSelector((state) => state.auth.devices);
  const reduxLoading = useSelector((state) => state.auth.loading);
  const reduxError = useSelector((state) => state.auth.error);
  const reduxSummaryCounts = useSelector((state) => state.auth.summaryCounts);
  const [summaryCounts, setSummaryCounts] = useState(reduxSummaryCounts);
  const reduxDeviceAlerts = useSelector(
    (state) => state.dashboard.deviceAlerts
  );
  const [deviceAlerts, setDeviceAlerts] = useState(
    reduxDeviceAlerts?.data || []
  );
  const [devices, setDevices] = useState(reduxDevices?.devices || []);
  const [loading, setLoading] = useState(reduxLoading);
  const [error, setError] = useState(reduxError);
  const reduxDashboardSearch = useSelector((state) => state.auth.dashboardSearch);
  const reduxDashboardPage = useSelector((state) => state.auth.dashboardPage);
  const [searchText, setSearchText] = useState(reduxDashboardSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState(reduxDashboardSearch || '');
  const reduxDashboardSortBy = useSelector((state) => state.auth.dashboardSortBy);
  const reduxDashboardOrder = useSelector((state) => state.auth.dashboardOrder);
  const [sortBy, setSortBy] = useState(reduxDashboardSortBy || '');
  const [order, setOrder] = useState(reduxDashboardOrder || 'ASC');
  const debounceTimeout = useRef();
  // Pagination state
  const [page, setPage] = useState(reduxDashboardPage || 1);
  const [limit] = useState(5);

  // Alert pagination state
  const [alertPage, setAlertPage] = useState(1);
  const [alertLimit] = useState(10);
  const alertListRef = useRef();
  const { kpiData, alertData } = useSocket();

  // Infinite scroll for alerts
  useEffect(() => {
    setRole(localStorage.getItem('role') || 'ADMIN');
    const handleScroll = () => {
      const el = alertListRef.current;
      if (!el) return;
      // Check if scrolled to bottom
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
        // Only fetch next page if there are more alerts
        if (deviceAlerts.length >= alertLimit * alertPage) {
          setAlertPage((prev) => prev + 1);
        }
      }
    };
    const el = alertListRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
    };
  }, [deviceAlerts, alertLimit, alertPage]);

  // Update summaryCounts with real-time KPI data
  useEffect(() => {
    if (kpiData?.data) {
      setSummaryCounts((prev) => ({ ...prev, ...kpiData.data }));
    }
  }, [kpiData]);

  useEffect(() => {
    // merge incoming alerts array into existing alerts array
    const incoming = alertData?.data?.alerts;
    if (Array.isArray(incoming) && incoming.length) {
      setDeviceAlerts((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        return [ ...incoming , ...prevArr];
      });
    }
  }, [alertData]);

  // Keep summaryCounts in sync with redux if redux changes (e.g. on page load)
  useEffect(() => {
    setSummaryCounts(reduxSummaryCounts);
  }, [reduxSummaryCounts]);

  useEffect(() => {
    
    if (reduxDeviceAlerts?.page) {
      setAlertPage(reduxDeviceAlerts.page);
    }
    // If fetching first page, replace; if fetching next page, append
    if (reduxDeviceAlerts?.page > 1) {
      setDeviceAlerts((prev) => [...prev, ...(reduxDeviceAlerts?.data || [])]);
    } else {
      setDeviceAlerts(reduxDeviceAlerts?.data || []);
    }
  }, [reduxDeviceAlerts]);

  useEffect(() => {
    dispatch(fetchSummaryCounts());
  }, [dispatch]);

  // Debounce searchText
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      dispatch(setDashboardSearch(searchText));
      dispatch(setDashboardPage(1)); // Reset to page 1 on search
    }, 500);
    return () => clearTimeout(debounceTimeout.current);
    // eslint-disable-next-line
  }, [searchText]);

  useEffect(() => {
    dispatch(fetchDevices({ page, limit, searchBy: debouncedSearch, sortBy: sortBy, order }));
    dispatch(setDashboardPage(page));
    dispatch(setDashboardSort({ sortBy, order }));
  }, [dispatch, page, limit, debouncedSearch, sortBy, order]);

  useEffect(() => {
    setDevices(reduxDevices?.data || []);
  }, [reduxDevices]);

  useEffect(() => {
    setLoading(reduxLoading);
  }, [reduxLoading]);


  useEffect(() => {
    setError(reduxError);
  }, [reduxError]);

  useEffect(() => {
    dispatch(
      fetchDeviceAlerts({
        limit: alertLimit,
        page: alertPage,
        alertType,
        deviceName: debouncedAlertSearch,
      })
    );
  }, [dispatch, alertLimit, alertPage,]);

  useEffect(() => {
    dispatch(
      fetchDeviceAlerts({
        limit: alertLimit,
        page: 1,
        alertType,
        deviceName: debouncedAlertSearch,
      })
    );
  }, [dispatch, alertType, debouncedAlertSearch]);

  const summaryData = [
    {
      icon: 'total_user',
      value: summaryCounts.totalCustomers,
      label: 'Total Customers',
      color: '#3ec3b5',
    },
    {
      icon: 'user_check_icon',
      value: summaryCounts.totalTechnicians,
      label: 'Total Technicians',
      color: '#bdbdbd',
    },
    {
      icon: 'wifi_on',
      value: summaryCounts.totalDeviceOnline,
      label: 'Devices Online',
      color: '#5c6bc0',
    },
    {
      icon: 'wifi_off',
      value: summaryCounts.totalDeviceOffline,
      label: 'Devices Offline',
      color: '#e97c9a',
    },
  ];

  function getSummaryIcon(type) {
    if (type === 'total_user')
      return <img src={totalUserIcon} alt="Total Users" />;
    if (type === 'user_check_icon')
      return <img src={userCheckIcon} alt="Technicians" />;
    if (type === 'wifi_on')
      return <img src={deviceOnIcon} alt="Devices Online" />;
    if (type === 'wifi_off')
      return <img src={deviceOffIcon} alt="Devices Offline" />;
  }

  // Reset alertPage to 1 on mount to avoid multiple API calls
  useEffect(() => {
    setAlertPage(1);
  }, []);

  return (
    <>
      <div className="dashboard-wrapper">
        <div className={`summary-info ${role === "TECHNICIAN" ? 'tech-info' : ''}`}>
          {summaryData
            .filter(item => !(role === 'TECHNICIAN' && item.icon === 'user_check_icon'))
            .map((item, idx) => (
              <div className="card-info" key={idx}>
                <div className={`icon-box bg-${idx}`}>
                  {getSummaryIcon(item.icon)}
                </div>
                <div className="text-box">
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              </div>
            ))}
        </div>
        <div className='device-section'>
          <div className='device-list'>
            <h2>Device List </h2>
            <div className='search-filter'>
              <div className='input-card'>
                <input
                  className="dashboard-table-search"
                  placeholder="Search by :- Device name or Location"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
                />
                <button><img src={searchIcon} alt="Search" /></button>
              </div>
            </div>

            <div className="device-list-table table-responsive">
              {loading ? (
                // <div style={{ padding: '24px', textAlign: 'center' }}>
                //   Loading devices...
                // </div>
                <Loader />
              ) : error ? (
                <div
                  style={{ padding: '24px', color: 'red', textAlign: 'center' }}
                >
                  {error}
                </div>
              ) : (
                devices.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>
                    No device found
                  </div>
                ) : (
                  <CommanTable
                    columns={[
                      { key: 'name', label: 'Device name', sortable: true },
                      { key: 'status', label: 'Status', sortable: true },
                      { key: 'city', label: 'City', sortable: true },
                    ]}
                    data={devices.map((d) => ({
                      id: d.id,
                      name: d.name,
                      status:
                        d.status === 'active' ? (
                          <span className="dashboard-status-active">Online</span>
                        ) : (
                          <span className="dashboard-status-inactive">Offline</span>
                        ),
                      city: (
                        <span>
                          <img
                            src={locationIcon}
                            alt="location"
                            style={{
                              width: 11,
                              height: 15,
                              marginRight: 4,
                              verticalAlign: 'middle',
                            }}
                          />
                          {d?.user?.city?.name}
                        </span>
                      ),
                      userId: d.user?.id,
                    }))}
                    onSort={(col) => {
                      // Toggle order if same column, else default to DES
                      setSortBy((prevSort) => {
                        if (prevSort === col) {
                          setOrder((prevOrder) => (prevOrder === 'DESC' ? 'ASC' : 'DESC'));
                          return col;
                        } else {
                          setOrder('DESC');
                          return col;
                        }
                      });
                    }}
                    sortBy={sortBy}
                    onRowClick={(row) => {
                      dispatch(setDeviceId(row.id)); // deviceId save in Redux
                      dispatch(setDeviceName(row.name)); // deviceName save in Redux
                      navigate(`/device/${row.id}`, { state: { userId: row.userId } });
                    }}
                  />
                )
              )}
            </div>
            {/* Pagination below table */}
            <Pagination
              page={page}
              totalPages={
                reduxDevices?.total ? Math.ceil(reduxDevices.total / limit) : 1
              }
              totalResults={reduxDevices?.total || 0}
              limit={limit}
              onPageChange={(p) => {
                setPage(p);
                dispatch(setDashboardPage(p));
              }}
            />

          </div>
          <div className='recent-alerts'>
            <h2>Recent Alerts</h2>
            <div className='search-filter'>
              <div className='input-card'>
                <input
                  className="dashboard-table-search"
                  placeholder="Search by :- Device name"
                  value={alertSearch}
                  onChange={(e) => {
                    setAlertSearch(e.target.value);
                    setAlertPage(1); // Reset pagination when search changes
                  }}
                />
                <button><img src={searchIcon} alt="Search" /></button>
              </div>
              <CustomDropdown
                options={ALERT_FILTER_OPTIONS}
                selectedValue={
                  ALERT_FILTER_OPTIONS.find(opt => opt.value === alertType)?.label || 'Select'
                }
                onSelect={value => setAlertType(value)}
              />

              {/* <select
                className="filter-select-dropdown"
                value={alertType}
                onChange={(e) => {
                  setAlertType(e.target.value);
                  setAlertPage(1); // Reset pagination when filter changes
                }}
              >
                {ALERT_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select> */}
            </div>
            <div className="dashboard-alerts-list" ref={alertListRef}>
              {Array.isArray(deviceAlerts) && deviceAlerts.length > 0 ? (
                deviceAlerts.map((alert, idx) => (
                  <div key={idx} className="dashboard-alert-item">
                    <div className='alert-dot' style={{ backgroundColor: alert?.color }}></div>
                    <div className="dashboard-alert-info">
                      <div className="dashboard-alert-name">
                        {alert?.device?.name}
                      </div>
                      <div className="dashboard-alert-desc">
                        {alert.message} {alert.alertType === "TDS" ? <span>{"- "}{Math.round(alert?.severity)} {" ppm"}</span> : null}
                      </div>
                    </div>
                    <div className="dashboard-alert-meta">
                      <div className="dashboard-alert-time">
                        {getTimeAgo(alert.createdAt)}
                      </div>
                      <span
                        className={`dashboard-alert-icon-${alert.alertType}`}
                      >
                        <img
                          src={alertTriangleIcon}
                          alt="Alert"
                          style={{
                            filter: hexToCssFilter(`${alert.color}`),
                          }}
                        // style={{
                        //   filter: alert?.color
                        //     ? `brightness(0) saturate(100%) drop-shadow(0 0 0 ${alert.color})`
                        //     : 'none',
                        // }}
                        />
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: '#888',
                  }}
                >
                  No recent alerts
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="dashboard-container responsive-dashboard">

        <Outlet />
      </div>
    </>
  );
};
export default Dashboard;
